import Modal from "../Modal";
import CustomButton from "../CustomButton";
import React, { useState, useEffect } from 'react';
import useReportProfile from "../../hooks/profile/useReportModal";
import { useTranslation } from "react-i18next";
import { useReportMutation } from '../../services/profileApis';
import { FiAlertCircle, FiCheckCircle, FiInfo } from "react-icons/fi";



const ReportProfileModal = ({ username }) => {
  const [reportMutation] = useReportMutation();
  const [selectedOption, setSelectedOption] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false); 
  const uReportModal = useReportProfile();
  const {t} = useTranslation('profile')
  const translation = t('post-user')
  const [isSubmitting, setIsSubmitting] = useState(false);
  

  // Reset state when modal reopens
  useEffect(() => {
    if (uReportModal.isOpen) {
      setSelectedOption('');
      setMessage('');
      setIsError(false);
      setIsSubmitting(false);
    }
  }, [uReportModal.isOpen]);

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    setMessage(''); // Clear previous messages when option changes
  };

  const clearMessage = () => {
    setMessage('');
    setIsError(false);
  };

  const handleSubmitReport = async () => {
    if (!selectedOption) {
      setMessage(`${translation.select_report_reason}`);
      setIsError(true);
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("username", username);
    formData.append('problem', selectedOption);

    try {
      const response = await reportMutation(formData).unwrap();
      setMessage(response.message || translation.report_success_mess);
      setIsError(false);
      setTimeout(() => uReportModal.close(), 2000);
    } catch (error) {
      setMessage(error.data?.error || translation.error_report);
      setIsError(true);
    } finally {
      setIsSubmitting(false);
      setTimeout(clearMessage, 5000);
    }
  };

  const renderOptions = () => {
    if (!translation.options) return null;
    
    return Object.entries(translation.options).map(([key, value]) => (
      <label 
        key={key}
        className={`
          flex items-center p-2 rounded-lg transition-colors
          hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer
          ${selectedOption === value ? 'bg-green-50 dark:bg-green-900' : ''}
        `}
      >
        <input
          type="radio"
          name="report-reason"
          value={value}
          checked={selectedOption === value}
          onChange={handleOptionChange}
          className="form-radio text-green-600 h-5 w-5 focus:ring-green-500"
          disabled={isSubmitting}
        />
        <span className="ml-3 text-gray-900 dark:text-gray-100">{value}</span>
      </label>
    ));
  };

  const renderMessage = () => (
    <div className={`
      mb-4 p-4 rounded-lg flex items-center space-x-3
      ${isError 
        ? "bg-red-50 text-red-800 dark:bg-red-900 dark:text-red-100"
        : "bg-green-50 text-green-800 dark:bg-green-900 dark:text-green-100"
      }
    `}>
      {isError ? (
        <FiAlertCircle className="flex-shrink-0 h-6 w-6" />
      ) : (
        <FiCheckCircle className="flex-shrink-0 h-6 w-6" />
      )}
      <span>{message}</span>
    </div>
  );

  const content = (
    <div className="space-y-4">
      {!message && (
        <>
          <div className="text-gray-600 dark:text-gray-300 mb-4">
            <FiInfo className="inline mr-2" />
            {translation.note}
          </div>
          
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {renderOptions()}
          </div>
        </>
      )}

      {message && renderMessage()}

      <div className="flex justify-end space-x-3 pt-4">
        <CustomButton
          label={translation.cancel}
          onClick={uReportModal.close}
          variant="outline"
          disabled={isSubmitting}
        />
        <CustomButton
          label={translation.submit}
          onClick={handleSubmitReport}
          loading={isSubmitting}
          disabled={!selectedOption || isSubmitting}
          icon={<FiAlertCircle className="w-5 h-5 mr-2" />}
        />
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={uReportModal.isOpen}
      close={uReportModal.close}
      label={translation.name}
      content={content}
    />
  );
};

export default ReportProfileModal;